/******************************************************************************
 *** BgtSpcAllAtrzPrgrsInq ** 특별회계기금 * 결재자용 * 수입/지출결의서 결재진행 조회 * TAB 구분
*******************************************************************************/
import React from "react";
import Util from "@/js/common/Util";
import SelectBox from "@/js/common/ui/input/SelectBox";
import StyledTabs from "@/js/common/ui/StyledTabs";
import { Tab, TabList, TabPanel } from "react-tabs";
import { useRecoilValue } from "recoil";
import * as Atom from "@/js/common/Atom";
import TextBox from "@/js/common/ui/input/TextBox";
import { systemTab } from "@/js/common/types/types";
import BgtSpcPrgrsDpstList from "./BgtSpcPrgrsDpstList";
import BgtSpcPrgrsExpndList from "./BgtSpcPrgrsExpndList";

const BgtSpcAllAtrzPrgrsInq = () => {
    const userInfo = useRecoilValue(Atom.userInfo);
    // 검색 조건
    const [upOgnzCd        , setUpOgnzCd        ] = React.useState<string>(userInfo.upOgnzCd);      // 상위조직코드(본부)
    const [ognzCd          , setOgnzCd          ] = React.useState<string>(userInfo.ognzCd  );      // 조직코드(지부)
    const [acntgYear       , setAcntgYear       ] = React.useState<string>(Util.getNowAcntgYear()); // 회계연도
    const [searchKywd      , setSearchKywd      ] = React.useState<string>("");                     // 검색어(적요)
    const [systemCode      , setSystemCode      ] = React.useState<string>(""); // 특별회계기금 시스템 코드
    const [searchAtrzSttsCd, setSearchAtrzSttsCd] = React.useState<string>("03"); // 진행상태(검색)

    // 특별회계기금 구분 Tabs
    const [spcTabIndex, setSpcTabIndex] = React.useState<number>(0); // 0: 복지기금, 1: 자립기금, 2: 발전기금, 3: 희생자 부조금, 4: 퇴직급여충당금
    // 수입/지출 Tabs
    const [tabIndex, setTabIndex] = React.useState<number>(0); // 0: 수입, 1: 지출

    // 시스템 사용 TAB
    const systemsTab: systemTab[] = [
        { id: "C1", title: "복지기금"      , visible: "N" },
        { id: "C2", title: "자립기금"      , visible: "N" },
        { id: "C3", title: "발전기금"      , visible: "N" },
        { id: "C4", title: "희생자 부조금" , visible: "N" },
        { id: "C5", title: "퇴직급여충당금", visible: "N" },
    ];
    const [groupTab, setGroupTab] = React.useState<systemTab[]>(systemsTab);

    // 시스템 사용 여부
    React.useEffect(() => {
        updateSystem("C1", userInfo.sysAuthrtC1Yn);
        updateSystem("C2", userInfo.sysAuthrtC2Yn);
        updateSystem("C3", userInfo.sysAuthrtC3Yn);
        updateSystem("C4", userInfo.sysAuthrtC4Yn);
        updateSystem("C5", userInfo.sysAuthrtC5Yn);
    }, [userInfo]);

    // 시스템 사용여부 변경
    const updateSystem = (targetId: string, newVal: string) => {
        setGroupTab((prev) =>
            prev.map((item) =>
                item.id === targetId ? { ...item, visible: newVal } : item
            )
        );
    };

    // groupTab이 변경될 때 첫 번째 id를 systemCode에 설정
    React.useEffect(() => {
        if (groupTab.length > 0) {
            setSystemCode(groupTab[0].id);
        }
    }, [groupTab]);

    return (
        <div className="w1200 bgcWhite p20 bdrd8 mt10 boxShadowSoft">
            {/* 타이틀 */}
            <div className="mt5">
                <span className="dpBlock w100p fm-c fs25 fc2c3e50 ls1 pb10">특별회계기금 결재진행 조회</span>
                <div className="bdt3 bdc007bff pb10" />
            </div>

            {/* 탭 영역 */}
            {groupTab.filter((sys) => sys.visible === "Y").length === 0 ? (
                <div className="fm-c p20 fc777777 fs16 taC mt10">
                    접근 가능한 메뉴가 없습니다.
                </div>
            ) : (
                <StyledTabs selectedIndex={spcTabIndex} className="tabFit">
                    <TabList>
                        {groupTab.filter((sys) => sys.visible === "Y").map((sys, idx) => (
                            <Tab key={sys.id} onClick={() => {
                                setSpcTabIndex(idx);
                                setSystemCode(sys.id)
                            }}>
                                {sys.title}
                            </Tab>
                        ))}
                    </TabList>
                </StyledTabs>
            )}

            {/* 검색 조건 */}
            <div className="bd1 bdcDDDDDD p20 mb10 bdrd6 bgcFAFAFA">
                <div className="dpFlx aiC flxWrap gap14">
                    {/* 소속 */}
                    <div className="dpFlx aiC">
                        <span className="w50 fm-c fs15 fc333333 ls1 taL">본부</span>
                        <SelectBox value={upOgnzCd}
                            width={150} height={36}
                            fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            background="#F6F6F6" paddingRight={8}
                            codeArray={Util.getUpOrgCodeArray("01")}
                            placeholder="전체"
                            onChangeFunc={(data: string) => setUpOgnzCd(data)}
                            disabled={true}
                        />
                    </div>

                    {/* 지부 */}
                    <div className="dpFlx aiC">
                        <span className="w50 fm-c fs15 fc333333 ls1 taL">지부</span>
                        <SelectBox value={ognzCd}
                            width={150} height={36}
                            fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            background="#F6F6F6"
                            codeArray={Util.getDownOrgCodeArray(upOgnzCd)}
                            placeholder="전체" paddingRight={8}
                            onChangeFunc={(data: string) => setOgnzCd(data)}
                            disabled={true}
                        />
                    </div>

                    {/* 회계연도 */}
                    <div className="dpFlx aiC">
                        <span className="w80 fm-c fs15 fc333333 ls1 taL">회계연도</span>
                        <SelectBox value={acntgYear}
                            width={120} height={36}
                            fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            background="#fff"
                            codeArray={Util.getAcntgYearArray()}
                            placeholder="선택" paddingRight={8}
                            onChangeFunc={(data: string) => setAcntgYear(data)}
                        />
                    </div>

                    {/* 진행상태 */}
                    <div className="dpFlx aiC">
                        <span className="w80 fm-c fs15 fc333333 ls1 taL">진행상태</span>
                        <SelectBox value={searchAtrzSttsCd}
                            width={140} height={36}
                            fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            background="#fff" paddingRight={8}
                            codeArray={[{ code: "", name: "전체" }].concat(Util.getCodeArray("ATRZ_STTS_CD").filter(item => item.code === '03' || item.code === '05'))}
                            placeholder="전체"
                            onChangeFunc={(data: string) => setSearchAtrzSttsCd(data)}
                        />
                    </div>

                    {/* 적요 */}
                    <div className="dpFlx aiC">
                        <span className="w50 fm-c fs15 fc333333 ls1 taL">적요</span>
                        <TextBox value={searchKywd}
                            width={150} height={36}
                            fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            style={{ backgroundColor: "#fff", padding: "0 12px" }}
                            placeholder="검색어 입력"
                            onChangeFunc={(text: string) => setSearchKywd(text)}
                        />
                    </div>
                </div>
            </div>

            {/* 탭 */}
            <StyledTabs selectedIndex={tabIndex} className="mb10 tabFit">
                <TabList>
                    <Tab onClick={() => setTabIndex(0)}>수입 결의서</Tab>
                    <Tab onClick={() => setTabIndex(1)}>지출 결의서</Tab>
                </TabList>

                {/* ========================= 수입 탭 ========================= */}
                <TabPanel>
                    <BgtSpcPrgrsDpstList
                        ognzCd           = {ognzCd          } // 조직
                        acntgYear        = {acntgYear       } // 회계연도
                        systemCode       = {systemCode      } // 시스테코드
                        searchKywd       = {searchKywd      } // 검색어(적요)
                        searchAtrzSttsCd = {searchAtrzSttsCd} // 검색(진행상태)
                    ></BgtSpcPrgrsDpstList>
                </TabPanel>

                {/* ========================= 지출 탭 ========================= */}
                <TabPanel>
                    <BgtSpcPrgrsExpndList
                        ognzCd           = {ognzCd          } // 조직
                        acntgYear        = {acntgYear       } // 회계연도
                        systemCode       = {systemCode      } // 시스테코드
                        searchKywd       = {searchKywd      } // 검색어(적요)
                        searchAtrzSttsCd = {searchAtrzSttsCd} // 검색(진행상태)
                    ></BgtSpcPrgrsExpndList>
                </TabPanel>
            </StyledTabs>
        </div>
    );
};

export default BgtSpcAllAtrzPrgrsInq;